import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../../lib/firebase';
import { refreshIdTokenBestEffort } from './refreshIdToken';

/**
 * 「自分のデータか」を端末引き継ぎ後も正しく判定するための identity ヘルパー (R-70 方針B、docs/98 R-70)。
 *
 * 各端末は匿名認証で別々の UID を持つ。端末引き継ぎ (recoverParticipant / consumeDeviceTransferToken) は
 * 旧 UID 群を `participants/{uid}/memberships/{districtId}.linkedFromUids` に推移閉包で蓄積する
 * (copyMemberships)。本フックはそれを **live (onSnapshot)** で読み、「自分 = 現 UID ∪ linkedFromUids」を返す。
 *
 * - **読取系 (一覧の onlyMine / 写真DL の対象 / 編集削除ボタンの表示判定 等) はこの allUids を使う**
 *   (membership doc 由来なので **上限なし** + タブ間 live 一貫)。
 * - **サーバ強制の owner-write (firestore.rules の編集/削除)** は別途 custom claim `linkedUids`
 *   (MRU 上限 {@link MAX_LINKED_UIDS} 件) を見る。claim は引き継ぎ実行タブで即時反映、他タブは
 *   token refresh まで遅延しうる (UI で注意喚起する方針)。読取は本フックで遅延なく一貫する。
 *
 * 判定は純粋関数 {@link isOwnedBy} に集約し、散在する `authorUid === uid` を置き換える。
 */
export interface MyIdentity {
  /** 現在の端末の UID (匿名認証)。未認証/未取得は null。 */
  uid: string | null;
  /** 過去端末の UID 群 (membership.linkedFromUids、引き継ぎで蓄積)。 */
  linkedUids: string[];
  /** uid + linkedUids (重複排除)。「自分のデータか」判定に使う全 UID。 */
  allUids: string[];
}

/**
 * `ownerUid` (post.authorUid / insight.userId 等) が自分のものか判定する純粋関数。
 * 現 UID または引き継ぎ元 UID のいずれかに一致すれば true。null/空は false。
 */
export function isOwnedBy(
  ownerUid: string | null | undefined,
  identity: Pick<MyIdentity, 'allUids'>,
): boolean {
  if (!ownerUid) return false;
  return identity.allUids.includes(ownerUid);
}

/**
 * 自分の identity (現 UID + 引き継ぎ元 UID 群) を返す。
 * @param uid 現在の匿名認証 UID (useAnonymousAuth 等から渡す)。
 * @param districtId 対象地区 (membership doc は地区単位)。
 */
export function useMyIdentity(uid: string | null, districtId: string | null): MyIdentity {
  const [linkedUids, setLinkedUids] = useState<string[]>([]);

  useEffect(() => {
    if (!uid || !districtId) {
      setLinkedUids([]);
      return;
    }
    const ref = doc(db, 'participants', uid, 'memberships', districtId);
    // R-70 P6 (双方向リンク): 別端末の復帰でこの端末の linkedFromUids と claim `linkedUids` が
    // サーバ側で更新される。閲覧系は本購読で即時に追従するが、編集/削除 (rules) は ID トークンの
    // 更新まで古い claim を見るため、購読開始後に一覧が変わったときだけ 1 回 best-effort で
    // トークンを強制更新する (初回配信では更新しない = 毎回のページ表示で無駄な通信をしない)。
    let seenFirst = false;
    let lastKey = '';
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const raw = snap.data()?.linkedFromUids;
        const next = Array.isArray(raw)
          ? raw.filter((x): x is string => typeof x === 'string' && x !== '')
          : [];
        setLinkedUids(next);
        const key = next.join(' ');
        if (!seenFirst) {
          seenFirst = true;
          lastKey = key;
          return;
        }
        if (key !== lastKey) {
          lastKey = key;
          refreshIdTokenBestEffort();
        }
      },
      () => setLinkedUids([]),
    );
    return unsub;
  }, [uid, districtId]);

  const allUids = useMemo(() => {
    if (!uid) return linkedUids.slice();
    return [uid, ...linkedUids.filter((x) => x !== uid)];
  }, [uid, linkedUids]);

  return { uid, linkedUids, allUids };
}
